/**
 * SpeakerManagement - CRUD for event speakers
 */
import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Mic, Linkedin, Twitter, Globe, Loader2, Upload, X } from 'lucide-react';
import { speakerAPI } from '@/lib/api';
import api from '@/lib/api';
import { MediaUpload } from '@/components/MediaUpload';

const STATUSES = ['active', 'inactive'];
const STATUS_COLORS = { active: 'bg-green-100 text-green-800', inactive: 'bg-gray-100 text-gray-800' };

// Photo upload component
const PhotoUpload = ({ value, onChange }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', 'general');

      const response = await api.post('/admin/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.url) {
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        const fullUrl = `${baseUrl}${response.data.url}`;
        onChange(fullUrl);
        toast.success('Photo uploaded!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <Label>Photo</Label>
      <div className="flex gap-2 items-start">
        <div className="flex-1 space-y-2">
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter URL or upload a photo"
          />
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".png,.jpg,.jpeg,.webp"
            className="hidden"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
              Upload Photo
            </Button>
            {value && (
              <Button type="button" variant="ghost" size="sm" onClick={() => onChange('')}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        {value && (
          <Avatar className="h-16 w-16">
            <AvatarImage src={value} />
            <AvatarFallback>IMG</AvatarFallback>
          </Avatar>
        )}
      </div>
    </div>
  );
};

// Form component extracted outside
const SpeakerForm = ({ speaker, onSubmit, onCancel }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(speaker || { 
    name: '', 
    email: '', 
    phone: '', 
    company: '', 
    title: '', 
    bio: '', 
    photo_url: '', 
    topics: [], 
    status: 'active', 
    linkedin_url: '', 
    twitter_url: '', 
    website_url: '', 
    is_keynote: false, 
    is_featured: true 
  });
  const [topicInput, setTopicInput] = useState('');
  const addTopic = () => { if (topicInput.trim()) { setForm({...form, topics: [...(form.topics || []), topicInput.trim()]}); setTopicInput(''); }};

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', 'general');

      const response = await api.post('/admin/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.url) {
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        const fullUrl = `${baseUrl}${response.data.url}`;
        setForm({...form, photo_url: fullUrl});
        toast.success('Photo uploaded!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
        <div><Label>Email</Label><Input value={form.email || ''} onChange={e => setForm({...form, email: e.target.value})} type="email" /></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div><Label>Company</Label><Input value={form.company || ''} onChange={e => setForm({...form, company: e.target.value})} /></div>
        <div><Label>Title</Label><Input value={form.title || ''} onChange={e => setForm({...form, title: e.target.value})} /></div>
        <div><Label>Status</Label>
          <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Photo upload section */}
      <div className="space-y-2">
        <MediaUpload value={form.photo_url || ''} onChange={(url) => setForm({ ...form, photo_url: url })} purpose="avatar" label="Photo" testId="speaker-photo-upload" previewClassName="h-20" />
      </div>

      <div><Label>Bio</Label><Textarea value={form.bio || ''} onChange={e => setForm({...form, bio: e.target.value})} rows={3} /></div>
      <div>
        <Label>Topics / Expertise</Label>
        <div className="flex gap-2">
          <Input value={topicInput} onChange={e => setTopicInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTopic())} placeholder="Add topic..." />
          <Button type="button" variant="outline" onClick={addTopic}>Add</Button>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">{(form.topics || []).map((tag, i) => <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setForm({...form, topics: form.topics.filter((_, j) => j !== i)})}>{tag} ×</Badge>)}</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div><Label>LinkedIn</Label><Input value={form.linkedin_url || ''} onChange={e => setForm({...form, linkedin_url: e.target.value})} /></div>
        <div><Label>Twitter</Label><Input value={form.twitter_url || ''} onChange={e => setForm({...form, twitter_url: e.target.value})} /></div>
        <div><Label>Website</Label><Input value={form.website_url || ''} onChange={e => setForm({...form, website_url: e.target.value})} /></div>
      </div>
      <div className="flex gap-6">
        <div className="flex items-center gap-2"><Switch checked={form.is_keynote} onCheckedChange={v => setForm({...form, is_keynote: v})} /><Label>Keynote Speaker</Label></div>
        <div className="flex items-center gap-2"><Switch checked={form.is_featured} onCheckedChange={v => setForm({...form, is_featured: v})} /><Label>Featured</Label></div>
      </div>
      <div className="flex justify-end gap-2 pt-4 sticky bottom-0 bg-white">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSubmit(form)} disabled={!form.name}>{speaker ? 'Update' : 'Add'} Speaker</Button>
      </div>
    </div>
  );
};

const SpeakerManagement = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const { data: speakers = [], isLoading } = useQuery({
    queryKey: ['speakers', eventId, filterStatus],
    queryFn: () => speakerAPI.adminList(eventId, filterStatus === 'all' ? undefined : filterStatus).then(r => {
      // Handle both {data: [...]} and [...] response formats
      const responseData = r.data;
      if (Array.isArray(responseData)) return responseData;
      if (responseData?.data && Array.isArray(responseData.data)) return responseData.data;
      return [];
    }),
  });

  const createMutation = useMutation({
    mutationFn: (data) => speakerAPI.create(eventId, data),
    onSuccess: () => { toast.success('Speaker added'); queryClient.invalidateQueries(['speakers', eventId]); setIsOpen(false); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => speakerAPI.update(eventId, id, data),
    onSuccess: () => { toast.success('Speaker updated'); queryClient.invalidateQueries(['speakers', eventId]); setEditingSpeaker(null); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed')
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => speakerAPI.delete(eventId, id),
    onSuccess: () => { toast.success('Speaker removed'); queryClient.invalidateQueries(['speakers', eventId]); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed')
  });

  if (isLoading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="speaker-management">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold flex items-center gap-2"><Mic className="h-6 w-6" />Speakers</h2>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Badge variant="outline">{speakers.length} speakers</Badge>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />Add Speaker</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Add Speaker</DialogTitle></DialogHeader>
            <SpeakerForm onSubmit={data => createMutation.mutate(data)} onCancel={() => setIsOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {speakers.map(speaker => (
          <Card key={speaker.id} data-testid={`speaker-${speaker.id}`}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <Avatar><AvatarImage src={speaker.photo_url} /><AvatarFallback>{speaker.name?.charAt(0)}</AvatarFallback></Avatar>
                  <div>
                    <CardTitle className="text-lg">{speaker.name}</CardTitle>
                    <p className="text-sm text-slate-500">{speaker.title} {speaker.company && `@ ${speaker.company}`}</p>
                  </div>
                </div>
                <Badge className={STATUS_COLORS[speaker.status]}>{speaker.status}</Badge>
              </div>
            </CardHeader>
            <CardContent className="text-sm">
              {speaker.bio && <p className="text-slate-600 line-clamp-2 mb-2">{speaker.bio}</p>}
              {speaker.expertise?.length > 0 && <div className="flex flex-wrap gap-1 mb-2">{speaker.expertise.slice(0, 3).map((t, i) => <Badge key={i} variant="outline" className="text-xs">{t}</Badge>)}</div>}
              <div className="flex gap-2 mt-2">
                {speaker.linkedin_url && <a href={speaker.linkedin_url} target="_blank" rel="noopener noreferrer"><Linkedin className="h-4 w-4 text-blue-600" /></a>}
                {speaker.twitter_url && <a href={speaker.twitter_url} target="_blank" rel="noopener noreferrer"><Twitter className="h-4 w-4 text-sky-500" /></a>}
                {speaker.website && <a href={speaker.website} target="_blank" rel="noopener noreferrer"><Globe className="h-4 w-4 text-slate-500" /></a>}
              </div>
              <div className="flex gap-1 mt-2">
                <Button variant="ghost" size="sm" onClick={() => setEditingSpeaker(speaker)}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(speaker.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {speakers.length === 0 && <p className="col-span-3 text-center text-slate-500 py-8">No speakers added yet.</p>}
      </div>

      <Dialog open={!!editingSpeaker} onOpenChange={() => setEditingSpeaker(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Edit Speaker</DialogTitle></DialogHeader>
          {editingSpeaker && <SpeakerForm speaker={editingSpeaker} onSubmit={data => updateMutation.mutate({ id: editingSpeaker.id, data })} onCancel={() => setEditingSpeaker(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpeakerManagement;
